import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getCandidateInfoThunk } from '../../../redux/Candidate/Candidate.thunk';

import { getCandidateSelected } from '../../../redux/Candidate/Candidate.slice';

import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';

import CardComponent from '../../../components/Card.component';
import CandidateInfoBlockComponent from '../../../components/Blocks/CandidateInfoBlock.component';
import SpinnerComponent from '../../../components/Spinner.component';
import FloatButtonComponent from '../../../components/FloatButton.component';
import RecordPreNewModalComponent from '../../../components/Modal/Candidate/Record/RecordPreNewModal.component';

import useAuthHook from '../../../hooks/useAuth.hook';
import { UserPermissions } from '../../../types/userPermissions.types';
import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../../../utils/getUserRolesByToken';

import PlusIcon from '../../../components/Icons/Plus.icon';
import AttachmentsBlockComponent from '../../../components/Blocks/AttachmentsBlockComponent.component';
import {
  StyledCandidateInfo,
  StyledCandidateInfoBlocks,
  StyledCandidateInfoTitle,
  StyledCandidatePage,
  StyledCandidatePageLeftSide,
  StyledCandidatePageRightSide,
  StyledNameContainer,
} from '../Candidate/Candidate.styled';
import { CandidateActionsPanel } from '../Candidate/components/CandidateActionsPanel.component';
import { CandidateRecords } from '../Candidate/components/CandidateRecord.component';
import { FavouriteButton } from '../../../components/Buttons/FavouriteButton/FavouriteButton.component';
import { UserRole } from '../../../types/userRole.types';

const CREATE_ICON_SIZE = 18;

const InvitationCandidatePage = () => {
  const { guid } = useParams();
  if (guid === undefined) {
    throw new Error(`Invitation guid must be present in route path.`);
  }

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { openModal } = useModalManager();
  const {
    user: { permissions: authenticatedUserPermissions, roles },
  } = useAuthHook();

  const { item: candidate, loading: isCandidateLoading } =
    useAppSelector(getCandidateSelected);

  useEffect(() => {
    // TODO: заменить на запрос по guid (invitation endpoint)
    dispatch(getCandidateInfoThunk(guid));
  }, [guid, dispatch]);

  const hasUserPermissionToEditCandidate = checkUserRequiredPermissions(
    authenticatedUserPermissions,
    [UserPermissions.CAN_EDIT_CANDIDATE],
  );

  const hasUserPermissionToDeleteCandidate = checkUserRequiredPermissions(
    authenticatedUserPermissions,
    [UserPermissions.CAN_DELETE_CANDIDATE],
  );

  const hasUserPermissionToViewCandidateRecord = checkUserRequiredPermissions(
    authenticatedUserPermissions,
    [UserPermissions.CAN_VIEW_CANDIDATE_RECORD],
  );

  const hasUserPermissionToCreateCandidateRecord = checkUserRequiredPermissions(
    authenticatedUserPermissions,
    [UserPermissions.CAN_CREATE_CANDIDATE_RECORD],
  );

  return (
    <StyledCandidatePage>
      <StyledCandidatePageLeftSide>
        <CandidateActionsPanel
          candidate={candidate}
          hasUserPermissionToEditCandidate={hasUserPermissionToEditCandidate}
          hasUserPermissionToDeleteCandidate={
            hasUserPermissionToDeleteCandidate
          }
        />
        <StyledCandidateInfoBlocks>
          <StyledNameContainer>
            <h1>
              {!!candidate && candidate.fullName ? candidate.fullName : ''}
            </h1>
            {checkUserRequiredRole(roles, [UserRole.HEAD, UserRole.DEV_HEAD]) &&
            !!candidate ? (
              <FavouriteButton
                id={guid}
                isFavourite={candidate.isFavourite}
              />
            ) : null}
          </StyledNameContainer>
          <CardComponent>
            <StyledCandidateInfo>
              <StyledCandidateInfoTitle>
                {t('Contacts')}:
              </StyledCandidateInfoTitle>
              {isCandidateLoading ? (
                <SpinnerComponent />
              ) : (
                <CandidateInfoBlockComponent
                  links={candidate && candidate.links ? candidate.links : null}
                  experience={
                    candidate && candidate.experience
                      ? candidate.experience
                      : null
                  }
                  email={candidate && candidate.email ? candidate.email : null}
                  phone={candidate && candidate.phone ? candidate.phone : null}
                  tags={candidate && candidate.tags ? candidate.tags : null}
                  metaRegions={
                    candidate && candidate.metaRegions
                      ? candidate.metaRegions
                      : null
                  }
                  english={
                    candidate && candidate.english ? candidate.english : null
                  }
                  hasUnreachableRegions={
                    candidate && candidate.hasUnreachableRegions ? candidate.hasUnreachableRegions : null
                  }
                  hasUnreachableRegionsVarning={false}
                />
              )}
            </StyledCandidateInfo>
          </CardComponent>
          {!isCandidateLoading && candidate?.attachments?.length > 0 && (
            <CardComponent>
              <StyledCandidateInfo>
                <StyledCandidateInfoTitle>
                  {t('Attachments')}:
                </StyledCandidateInfoTitle>
                <AttachmentsBlockComponent
                  attachments={candidate.attachments}
                />
              </StyledCandidateInfo>
            </CardComponent>
          )}
        </StyledCandidateInfoBlocks>
      </StyledCandidatePageLeftSide>

      <StyledCandidatePageRightSide>
        {hasUserPermissionToViewCandidateRecord && (
          <CandidateRecords candidateId={guid} />
        )}

        {hasUserPermissionToCreateCandidateRecord && (
          <FloatButtonComponent
            onClick={() =>
              openModal(
                RecordPreNewModalComponent,
                { mergePerson: candidate },
                { position: 'right-bottom' },
              )
            }
          >
            <PlusIcon width={CREATE_ICON_SIZE} height={CREATE_ICON_SIZE} />
          </FloatButtonComponent>
        )}
      </StyledCandidatePageRightSide>
    </StyledCandidatePage>
  );
};

export default InvitationCandidatePage;
